import React, { useCallback } from 'react';
import classNames from 'classnames';
import { Tooltip, Button, makeStyles, shorthands } from '@fluentui/react-components';
import { ChevronLeftRegular, ChevronRightRegular } from '@fluentui/react-icons';

import { EmptyCarouselContent } from './empty-content';
import { CarouselItem } from './carousel-item';
import { TActiveItem } from '../types';

export const useStyles = makeStyles({
  carouselFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    ...shorthands.gap('5px'),
  },
});

interface ICardContentProps {
  activeItems: TActiveItem[];
  currentSlide: number;
  setCurrentSlide: (slide: number) => void;
  i18n: {
    getString: (key: string, params?: any) => string;
  };
  consultationLink: string;
  i18nErrors: object;
  serverContext: any;
  permissions: any;
  scope: {
    levelAt: string;
  };
  isMCC: boolean;
  currentActivity: any;
  logOpportunityEvent: any;
  preferencesService: any;
  locationMap: any;
  overallOptimizationScoreBar: object;
  viewRecommendation: Function;
  legacyConfig: any;
  walkme: any;
  jsdata: any;
  instrumentationConstants: any,
  instrumentationLegacy: any,
  logSlideDisplay: ({ reason }: { reason: string }) => void;
  adDisplayUrlService: any;
  removeTipLocally: (tipId: string) => void;
}

export const CardContent = ({
  activeItems,
  currentSlide,
  setCurrentSlide,
  i18n,
  consultationLink,
  i18nErrors,
  serverContext,
  permissions,
  scope,
  isMCC,
  currentActivity,
  logOpportunityEvent,
  preferencesService,
  locationMap,
  overallOptimizationScoreBar,
  viewRecommendation,
  legacyConfig,
  walkme,
  jsdata,
  instrumentationConstants,
  instrumentationLegacy,
  logSlideDisplay,
  adDisplayUrlService,
  removeTipLocally,
}: ICardContentProps) => {
  const classes = useStyles();
  const haveVideo = activeItems.some(item => item.helpType === 'video');
  const size = activeItems?.length || 0;
  const goPrev = useCallback(() => setCurrentSlide(currentSlide > 0 ? currentSlide - 1 : size - 1), [currentSlide, setCurrentSlide, size]);
  const goNext = useCallback(() => setCurrentSlide((currentSlide + 1) % size), [currentSlide, setCurrentSlide, size]);

  return (
    <div className={classNames('tipsCarousel', 'carousel', 'slide')} data-ride="carousel" data-interval="false" aria-label={i18n.getString(_TL_('Tips'))}>
      <ul className="carousel-inner" role="presentation">
        {activeItems?.length > 0 ? (
          activeItems.map((item, i) => (
            <li key={item.tipId || item.type} className={classNames('item', { active: i === currentSlide })} id={`carousel-panel-${i}`} tabIndex={-1}>
              <CarouselItem
                item={item}
                i18n={i18n}
                i18nErrors={i18nErrors}
                serverContext={serverContext}
                permissions={permissions}
                scope={scope}
                isMCC={isMCC}
                currentActivity={currentActivity}
                logOpportunityEvent={logOpportunityEvent}
                preferencesService={preferencesService}
                locationMap={locationMap}
                overallOptimizationScoreBar={overallOptimizationScoreBar}
                viewRecommendation={viewRecommendation}
                legacyConfig={legacyConfig}
                walkme={walkme}
                jsdata={jsdata}
                instrumentationConstants={instrumentationConstants}
                instrumentationLegacy={instrumentationLegacy}
                logSlideDisplay={logSlideDisplay}
                adDisplayUrlService={adDisplayUrlService}
                removeTipLocally={removeTipLocally}
              />
            </li>
          ))
        ) : (
          <EmptyCarouselContent i18n={i18n} consultationLink={consultationLink} />
        )}
      </ul>

      {haveVideo && <script>{'if (typeof ApexVideoHandler !== \'undefined\') {ApexVideoHandler.Init();}'}</script>}

      {activeItems?.length > 1 && (
        <div className={classNames('carousel-footer', classes.carouselFooter)}>
          <span className="carouselItemCount">
            {i18n.getString(_TL_('{{itemIndex}} of {{itemCount}}'), { itemIndex: currentSlide + 1, itemCount: activeItems.length })}
          </span>
          <Tooltip content={i18n.getString(_TL_('Previous'))} relationship="label">
            <Button className="previous" icon={<ChevronLeftRegular />} appearance="transparent" onClick={goPrev} />
          </Tooltip>
          <Tooltip content={i18n.getString(_TL_('Next'))} relationship="label">
            <Button className="next" icon={<ChevronRightRegular />} appearance="transparent" onClick={goNext} />
          </Tooltip>
        </div>
      )}

      {/* TODO: a help link for customer page, this is not in scope for now */}
      {/* <a className="HelpTopic hiddenHelpBridge" query={isCustomerLevel ? 'help:ext60045' : 'help:app54555'} href="javascript:void(0);" style={{ display: 'none' }} /> */}
    </div>
  );
};
